import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { prefersReducedMotion } from './motion-prefs';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  life: number;
  dec: number;
  r: number;
  c: string;
  rot: number;
  vr: number;
  shape: 'star' | 'rect';
}

const COLORS = ['#951E30', '#E9C58C', '#FBF4E6', '#c33a4f', '#F7E3B8', '#6E1222'];

function drawStar(ctx: CanvasRenderingContext2D, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    const rr = i % 2 ? r * 0.35 : r;
    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
}

/** Конфетти при выигрыше (портировано из js/main.js, раздел «fx: confetti»). */
export function useConfetti(canvasRef: RefObject<HTMLCanvasElement | null>) {
  const particlesRef = useRef<Particle[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = 0;
    let height = 0;
    let raf = 0;

    const size = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    const onResize = () => size();
    window.addEventListener('resize', onResize);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      particlesRef.current = particlesRef.current.filter((p) => p.life > 0);
      for (const p of particlesRef.current) {
        p.vy += p.g;
        p.vx *= 0.985;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life -= p.dec;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.c;
        if (p.shape === 'star') drawStar(ctx, p.r);
        else ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2);
        ctx.restore();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, [canvasRef]);

  return useCallback(
    (x: number, y: number, n = 90, power = 9) => {
      if (prefersReducedMotion()) return;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = Math.random() * power + 2;
        particlesRef.current.push({
          x,
          y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s - 4,
          g: 0.25,
          life: 1,
          dec: Math.random() * 0.012 + 0.008,
          r: Math.random() * 6 + 3,
          c: COLORS[i % COLORS.length],
          rot: Math.random() * 6,
          vr: (Math.random() - 0.5) * 0.4,
          shape: Math.random() < 0.35 ? 'star' : 'rect',
        });
      }
    },
    [],
  );
}
